import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '@platewell/schema';
import { useState, type FormEvent } from 'react';
import { resetPassword } from './lib/api.js';

/**
 * Completes a password reset from the emailed link (`?token=…`).
 *
 * Rendered outside the signed-in app shell so the link works before (and without) a session.
 */
export function ResetPassword({ onDone }: { onDone: () => void }) {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const linkError = params.get('error');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    linkError != null ? 'This reset link is invalid or has expired.' : token == null ? 'Missing reset token.' : null,
  );
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (token == null) return;
    if (password.length < MIN_PASSWORD_LENGTH || password.length > MAX_PASSWORD_LENGTH) {
      setError(`Password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not reset password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <form className="auth-card" onSubmit={(event) => void submit(event)}>
        <div className="brand">
          <img className="brand-mark" src="/logo-256.png" alt="" width={128} height={128} /> Platewell
        </div>

        {done ? (
          <>
            <p className="hint">Your password has been updated. You can sign in with it now.</p>
            <button type="button" className="btn primary" onClick={onDone}>
              Sign in
            </button>
          </>
        ) : (
          <>
            <p className="hint">Choose a new password for your account.</p>
            <div className="field">
              <label className="label" htmlFor="new-password">
                New password
              </label>
              <input
                id="new-password"
                className="input"
                type="password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                maxLength={MAX_PASSWORD_LENGTH}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={token == null || linkError != null}
              />
              <p className="hint">
                Password must be between {MIN_PASSWORD_LENGTH} and {MAX_PASSWORD_LENGTH} characters.
              </p>
            </div>
            <div className="field">
              <label className="label" htmlFor="confirm-password">
                Confirm password
              </label>
              <input
                id="confirm-password"
                className="input"
                type="password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                maxLength={MAX_PASSWORD_LENGTH}
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                disabled={token == null || linkError != null}
              />
            </div>
            <button
              className="btn primary"
              type="submit"
              disabled={busy || token == null || linkError != null || password.length === 0}
            >
              {busy ? 'Saving…' : 'Update password'}
            </button>
            <button type="button" className="btn ghost" onClick={onDone}>
              Back to sign in
            </button>
          </>
        )}

        {error != null && <p className="error-text">{error}</p>}
      </form>
    </div>
  );
}
